import { useContext, createContext, useEffect, useState } from 'react';
import LandService, { LandClass } from '../land';
import { UserClass } from '../user';
import { ProfileContext } from './';

import Loading from '../../components/auth/shared/loader';

interface DataContextInterface {
    lands: LandClass[];
    getLand: (landId: string) => LandClass | undefined;
    getLands: (forcedFromServer?: boolean) => Promise<LandClass[]>;
    setLands: (data: LandClass[]) => void;
    addLand: (land: LandClass) => Promise<LandClass[]>;
    updateLand: (landId: string) => void;
    removeLand: (land: LandClass) => LandClass[];
}

const DataContext = createContext({} as DataContextInterface);

const DataContextProvider = (props: { children: React.ReactElement }) => {
    const landService = new LandService();
    const { getProfileById, getProfilesById } = useContext(ProfileContext);

    const [isLoading, setLoading] = useState(true);
    const [lands, setLands] = useState<LandClass[]>([]);

    const addLandUsers = async (land: LandClass): Promise<LandClass> => {
        land.ownerUser = (await getProfileById(land.owner)) as UserClass;
        land.editorsUser = land.editors?.length
            ? ((await getProfilesById(land.editors)) as UserClass[])
            : [];
        land.readersUser = land.readers?.length
            ? ((await getProfilesById(land.readers)) as UserClass[])
            : [];
        return land;
    };

    const getLand = (landId: string): LandClass => {
        return lands.find((land: LandClass) => land.id === landId)!;
    };

    const getLands = async (): Promise<LandClass[]> => {
        setLoading(true);
        const allLands = (await landService.getMyLands()) as LandClass[];
        const landsDoc = await Promise.all(
            allLands.map(async (landDoc: any) => {
                return addLandUsers(landDoc);
            })
        );
        setLands(landsDoc);
        setLoading(false);
        return landsDoc;
    };

    const addLand = async (land: LandClass): Promise<LandClass[]> => {
        const allLands = [
            ...(lands as LandClass[]),
            (await addLandUsers(land)) as LandClass
        ];
        setLands(allLands);
        return allLands;
    };

    const updateLand = async (landId: string): Promise<LandClass> => {
        const updateLand = (await landService.get(landId)) as LandClass;
        const landsDoc = await Promise.all(
            lands.map(async (landDoc: any) => {
                return landDoc.id === updateLand.id
                    ? addLandUsers(updateLand)
                    : landDoc;
            })
        );
        setLands(landsDoc);
        return updateLand;
    };

    const removeLand = (land: LandClass): LandClass[] => {
        const result = lands.filter((l) => l.id !== land.id);
        setLands(result);
        return result;
    };

    useEffect(() => {
        getLands();
    }, []);

    return !isLoading ? (
        <DataContext.Provider
            value={{
                lands,
                getLand,
                getLands,
                setLands,
                addLand,
                updateLand,
                removeLand
            }}
        >
            {props.children}
        </DataContext.Provider>
    ) : (
        <Loading />
    );
};

export default DataContext;
export { DataContextProvider };
